package dev.kuml.portal.web.i18n.strings

/** R-21: strings for the "Teilen"-Dialog ([dev.kuml.portal.web.share.shareDialog]) and the public
 * betrachter-Seite ([dev.kuml.portal.web.share.publicShareScreen]). */
public interface ShareStrings {
    public val shareButton: String
    public val shareAriaLabel: String
    public val dialogTitle: String
    public val existingLinksHeading: String
    public val noLinksYet: String
    public val neverExpires: String
    public val alreadyExpired: String
    public val notYetViewed: String
    public val passwordProtectedBadge: String
    public val revokeButton: String
    public val revokeConfirmQuestion: String
    public val replaceButton: String
    public val newLinkHeading: String
    public val passwordCheckboxLabel: String
    public val passwordPlaceholder: String
    public val expiryCheckboxLabel: String
    public val createButton: String
    public val creating: String
    public val resultHint: String
    public val copyButton: String
    public val copied: String
    public val createFailed: String

    public val publicLoading: String
    public val publicPasswordPromptTitle: String
    public val publicPasswordPlaceholder: String
    public val publicUnlockButton: String
    public val publicUnlocking: String
    public val publicWrongPassword: String
    public val publicNotFoundTitle: String
    public val publicNotFoundDetail: String
    public val publicEmptyTitle: String
    public val publicEmptyDetail: String
    public val publicRenderFailed: String
    public val publicPoweredByLink: String
    public val publicDownloadSvg: String

    public fun expiresOn(date: String): String

    public fun viewedOn(date: String): String

    public val allPlainStrings: List<String>
        get() =
            listOf(
                shareButton,
                shareAriaLabel,
                dialogTitle,
                existingLinksHeading,
                noLinksYet,
                neverExpires,
                alreadyExpired,
                notYetViewed,
                passwordProtectedBadge,
                revokeButton,
                revokeConfirmQuestion,
                replaceButton,
                newLinkHeading,
                passwordCheckboxLabel,
                passwordPlaceholder,
                expiryCheckboxLabel,
                createButton,
                creating,
                resultHint,
                copyButton,
                copied,
                createFailed,
                publicLoading,
                publicPasswordPromptTitle,
                publicPasswordPlaceholder,
                publicUnlockButton,
                publicUnlocking,
                publicWrongPassword,
                publicNotFoundTitle,
                publicNotFoundDetail,
                publicEmptyTitle,
                publicEmptyDetail,
                publicRenderFailed,
                publicPoweredByLink,
                publicDownloadSvg,
            )
}

public object ShareStringsDe : ShareStrings {
    override val shareButton = "Teilen"
    override val shareAriaLabel = "Diagramm über einen Link teilen"
    override val dialogTitle = "Diagramm teilen"
    override val existingLinksHeading = "Bestehende Links"
    override val noLinksYet = "Noch keine Freigabe-Links für dieses Diagramm."
    override val neverExpires = "Läuft nie ab"
    override val alreadyExpired = "Abgelaufen"
    override val notYetViewed = "Noch nicht geöffnet"
    override val passwordProtectedBadge = "Passwortgeschützt"
    override val revokeButton = "Widerrufen"
    override val revokeConfirmQuestion = "Diesen Link widerrufen? Er funktioniert danach nicht mehr."
    override val replaceButton = "Ersetzen"
    override val newLinkHeading = "Neuen Link erzeugen"
    override val passwordCheckboxLabel = "Mit Passwort schützen"
    override val passwordPlaceholder = "Passwort (mind. 8 Zeichen)"
    override val expiryCheckboxLabel = "Ablaufdatum setzen"
    override val createButton = "Link erzeugen"
    override val creating = "Wird erzeugt …"
    override val resultHint =
        "Dieser Link wird nur jetzt angezeigt. Kopieren Sie ihn — später kann er nicht erneut eingeblendet werden."
    override val copyButton = "Kopieren"
    override val copied = "Kopiert."
    override val createFailed = "Der Link konnte nicht erzeugt werden."

    override val publicLoading = "Diagramm wird geladen …"
    override val publicPasswordPromptTitle = "Dieser Link ist passwortgeschützt"
    override val publicPasswordPlaceholder = "Passwort"
    override val publicUnlockButton = "Entsperren"
    override val publicUnlocking = "Wird geprüft …"
    override val publicWrongPassword = "Das Passwort ist nicht korrekt."
    override val publicNotFoundTitle = "Link ungültig"
    override val publicNotFoundDetail = "Dieser Link ist ungültig, abgelaufen oder wurde widerrufen."
    override val publicEmptyTitle = "Noch kein Diagramm"
    override val publicEmptyDetail = "Für diesen Link wurde noch kein Diagramminhalt gespeichert."
    override val publicRenderFailed = "Dieses Diagramm kann derzeit nicht dargestellt werden."
    override val publicPoweredByLink = "Erstellt mit kUML Portal"
    override val publicDownloadSvg = "SVG herunterladen"

    override fun expiresOn(date: String): String = "Läuft ab am $date"

    override fun viewedOn(date: String): String = "Zuletzt geöffnet: $date"
}

public object ShareStringsEn : ShareStrings {
    override val shareButton = "Share"
    override val shareAriaLabel = "Share this diagram via a link"
    override val dialogTitle = "Share diagram"
    override val existingLinksHeading = "Existing links"
    override val noLinksYet = "No share links for this diagram yet."
    override val neverExpires = "Never expires"
    override val alreadyExpired = "Expired"
    override val notYetViewed = "Not yet opened"
    override val passwordProtectedBadge = "Password-protected"
    override val revokeButton = "Revoke"
    override val revokeConfirmQuestion = "Revoke this link? It will stop working afterwards."
    override val replaceButton = "Replace"
    override val newLinkHeading = "Create a new link"
    override val passwordCheckboxLabel = "Protect with a password"
    override val passwordPlaceholder = "Password (min. 8 characters)"
    override val expiryCheckboxLabel = "Set an expiry date"
    override val createButton = "Create link"
    override val creating = "Creating …"
    override val resultHint = "This link is shown only now. Copy it — it cannot be shown again later."
    override val copyButton = "Copy"
    override val copied = "Copied."
    override val createFailed = "The link could not be created."

    override val publicLoading = "Loading diagram …"
    override val publicPasswordPromptTitle = "This link is password-protected"
    override val publicPasswordPlaceholder = "Password"
    override val publicUnlockButton = "Unlock"
    override val publicUnlocking = "Checking …"
    override val publicWrongPassword = "That password is incorrect."
    override val publicNotFoundTitle = "Invalid link"
    override val publicNotFoundDetail = "This link is invalid, expired, or has been revoked."
    override val publicEmptyTitle = "No diagram yet"
    override val publicEmptyDetail = "No diagram content has been saved for this link yet."
    override val publicRenderFailed = "This diagram cannot currently be displayed."
    override val publicPoweredByLink = "Made with kUML Portal"
    override val publicDownloadSvg = "Download SVG"

    override fun expiresOn(date: String): String = "Expires on $date"

    override fun viewedOn(date: String): String = "Last opened: $date"
}
